import { useCallback, useEffect, useState } from 'react'
import type ReactQuill from 'react-quill'

/**
 * Tracks whether the user has actually edited a Quill note.
 * Ignores programmatic content loads/normalizations (source !== 'user').
 * `version` counts user edits (and markDirty calls) so autosave can tell "changed since the last save".
 */
export const useNoteEditorDirty = (
  quillRef: React.RefObject<ReactQuill | null>,
  resetKey: string | number | null | undefined,
  enabled = true
) => {
  const [version, setVersion] = useState(0)

  useEffect(() => {
    setVersion(0)
  }, [resetKey])

  useEffect(() => {
    if (!enabled) return

    let disposed = false
    let editor: ReturnType<ReactQuill['getEditor']> | null = null

    const onTextChange = (_delta: unknown, _oldDelta: unknown, source: string) => {
      if (source === 'user') setVersion(v => v + 1)
    }

    const attach = () => {
      if (disposed) return
      editor = quillRef.current?.getEditor() ?? null
      if (!editor) {
        requestAnimationFrame(attach)
        return
      }
      editor.on('text-change', onTextChange)
    }

    attach()

    return () => {
      disposed = true
      editor?.off('text-change', onTextChange)
    }
  }, [enabled, quillRef, resetKey])

  const markDirty = useCallback(() => setVersion(v => v + 1), [])

  return { markDirty, version }
}
